import React from 'react';
import Button from '@/components/ui/Button';
import DelayedLoading from '@/components/ui/DelayedLoading';
import EmptyState from '@/components/ui/EmptyState';
import { SkeletonDetail } from '@/components/ui/LoadingSkeleton';

export type DetailSectionState =
  | { status: 'loading'; message: string }
  | { status: 'error'; message: string; retry: () => void; retryLabel?: string }
  | { status: 'empty'; message: string }
  | { status: 'success'; content: React.ReactNode };

export interface DetailSectionProps {
  state?: DetailSectionState;
  title?: string;
  children?: React.ReactNode;
}

const DetailSection: React.FC<DetailSectionProps> = ({ state, title, children }) => {
  if (!state) {
    return <section className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-6">{title && <h2 className="mb-4 text-lg font-semibold text-gray-950 dark:text-white">{title}</h2>}{children}</section>;
  }
  if (state.status === 'loading') {
    return (
      <DelayedLoading loading>
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-6">
          <SkeletonDetail label={state.message} />
        </div>
      </DelayedLoading>
    );
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title={state.message}
        action={(
          <Button className="min-h-11 motion-reduce:transition-none" onClick={state.retry}>
            {state.retryLabel ?? 'Retry'}
          </Button>
        )}
      />
    );
  }

  if (state.status === 'empty') {
    return <EmptyState title={state.message} />;
  }

  return <>{state.content}</>;
};

export default DetailSection;
